'use client'

import { useState } from 'react'
import { Linkedin, Facebook, LinkIcon, Check } from 'lucide-react'

interface ShareButtonsProps {
  title: string
  slug: string
}

function XIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" className={className} fill="currentColor">
      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
    </svg>
  )
}
export function ShareButtons({ title, slug }: ShareButtonsProps) {
  const [copied, setCopied] = useState(false)

  const getShareUrl = () => {
    if (typeof window !== 'undefined') {
      return `${window.location.origin}${window.location.pathname}`
    }
    const base = process.env.NEXT_PUBLIC_SERVER_URL || 'https://serottelaw.com'
    return `${base}/articles/${slug}`
  }

  const handleTwitterShare = () => {
    const shareUrl = getShareUrl()
    const encodedUrl = encodeURIComponent(shareUrl)
    const encodedTitle = encodeURIComponent(title)
    const twitterUrl = `https://twitter.com/intent/tweet?text=${encodedTitle}&url=${encodedUrl}`
    window.open(twitterUrl, '_blank', 'noopener,noreferrer,width=600,height=400')
  }

  const handleLinkedInShare = () => {
    const shareUrl = getShareUrl()
    const encodedUrl = encodeURIComponent(shareUrl)
    const linkedInUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`
    window.open(linkedInUrl, '_blank', 'noopener,noreferrer,width=600,height=600')
  }

  const handleFacebookShare = () => {
    const shareUrl = getShareUrl()
    const encodedUrl = encodeURIComponent(shareUrl)
    const facebookUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`
    window.open(facebookUrl, '_blank', 'noopener,noreferrer,width=600,height=400')
  }

  const handleCopyLink = async () => {
    const shareUrl = getShareUrl()
    try {
      await navigator.clipboard.writeText(shareUrl)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch (_err) {
      // Fallback for older browsers
      const textArea = document.createElement('textarea')
      textArea.value = shareUrl
      document.body.appendChild(textArea)
      textArea.select()
      document.execCommand('copy')
      document.body.removeChild(textArea)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    }
  }

  return (
    <div className="flex items-center gap-3">
      <button
        onClick={handleLinkedInShare}
        className="group w-10 h-10 rounded-full bg-white border border-primary/20 hover:bg-primary transition-colors flex items-center justify-center"
        aria-label="Share on LinkedIn"
      >
        <Linkedin className="w-5 h-5 text-gray-700 group-hover:text-white" />
      </button>
      <button
        onClick={handleTwitterShare}
        className="group w-10 h-10 rounded-full bg-white border border-primary/20 hover:bg-primary transition-colors flex items-center justify-center"
        aria-label="Share on X"
      >
        <XIcon className="w-5 h-5 text-gray-700 group-hover:text-white" />
      </button>
      <button
        onClick={handleFacebookShare}
        className="group w-10 h-10 rounded-full bg-white border border-primary/20 hover:bg-primary transition-colors flex items-center justify-center"
        aria-label="Share on Facebook"
      >
        <Facebook className="w-5 h-5 text-gray-700 group-hover:text-white" />
      </button>
      <button
        onClick={handleCopyLink}
        className="group w-10 h-10 rounded-full bg-white border border-primary/20 hover:bg-primary transition-colors flex items-center justify-center relative"
        aria-label="Copy Link"
      >
        {copied ? (
          <Check className="w-5 h-5 text-green-600 group-hover:text-white" />
        ) : (
          <LinkIcon className="w-5 h-5 text-gray-700 group-hover:text-white" />
        )}
      </button>
    </div>
  )
}
